/**
 * Parses GET /evals/scorers into UI types.
 *
 * Contract: `[{ name, displayName, variants: [{ name, requiredParameters[] }] }]`
 * or `{ content: [...] }` like other paginated experiment responses.
 */

import { extractArrayFromResponse } from './base';
import type { Scorer, ScorerVariant } from './types';

function parseVariant(raw: unknown): ScorerVariant | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const v = raw as Record<string, unknown>;
  const name = typeof v.name === 'string' ? v.name.trim() : '';
  if (!name) {
    return null;
  }

  const rp = v.requiredParameters;
  const requiredParameters = Array.isArray(rp)
    ? rp.filter((p): p is string => typeof p === 'string')
    : [];

  return { name, requiredParameters };
}

function parseScorer(raw: unknown): Scorer | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const name = typeof o.name === 'string' ? o.name.trim() : '';
  if (!name) {
    return null;
  }

  const displayName =
    typeof o.displayName === 'string' && o.displayName.trim()
      ? o.displayName.trim()
      : name;

  const variantsRaw = o.variants;
  const variants = Array.isArray(variantsRaw)
    ? variantsRaw
        .map(parseVariant)
        .filter((x): x is ScorerVariant => x !== null)
    : [];

  return { name, displayName, variants };
}

export function normalizeAvailableScorersPayload(data: unknown): Scorer[] {
  return extractArrayFromResponse<unknown>(data)
    .map(parseScorer)
    .filter((s): s is Scorer => s !== null);
}
